import { ColumnMatcherTypes } from "../../../types/rules/ColumnMatcherTypes";
import { DlfMatcherTypes } from "../../../types/rules/DlfMatcherTypes";
import { MatcherTypes } from "../../../types/rules/MatcherTypes";
import { ColumnMatcher, ColumnMatcherData } from "../../ColumnMatcher";
import { DocFieldConfig } from "../../config/DocFieldConfig";
import {
  DefaultValueMatcher,
  DefaultValueMatcherData,
} from "../DefaultValueMatcher";
import { DlfMatcher, DlfMatcherProperties } from "../DlfMatcher";
import { FuzzyFieldMatcher, FuzzyFieldMatcherData } from "../FuzzyFieldMatcher";
import { FuzzyMatcher, FuzzyMatcherData } from "../FuzzyMatcher";
import { KeyValueMatcher, KeyValueMatcherData } from "../KeyValueMatcher";
import { Matcher, MatcherProperties } from "../Matcher";
import { PageSection } from "../PageSection";
import { RegexFieldMatcher, RegexFieldMatcherData } from "../RegexFieldMatcher";
import { RegexMatcher, RegexMatcherData } from "../RegexMatcher";
import { SectionMatcher, SectionMatcherData } from "../SectionMatcher";
import { LocationMatcher, LocationMatcherData } from "../LocationMatcher";

type DefaultMatcher = KeyValueMatcher;
class MatcherFactory {
  static createMatcher(matcher: MatcherProperties): Matcher {
    //console.trace('Creating DLFMatcher');
    switch (matcher.discriminator) {
      case "M_REGEX": {
        return new RegexMatcher({
          regex: "",
          ...matcher,
        } as RegexMatcherData);
      }
      case "M_FUZZY": {
        return new FuzzyMatcher({
          fuzzyKey: "",
          ...matcher,
        } as FuzzyMatcherData);
      }
      case "M_LOCATION": {
        return new LocationMatcher(matcher as unknown as LocationMatcherData);
      }
      default: {
        throw new Error(`Unsupported Matcher Type ${matcher.discriminator}`);
      }
    }
  }

  static changeMatcher(
    matcher: MatcherProperties,
    newType: MatcherTypes,
    threshold?: number,
  ): Matcher {
    //console.debug('Changing DLFMatcher type');
    switch (newType as MatcherTypes) {
      case "M_FUZZY": {
        return new FuzzyMatcher({
          discriminator: "M_FUZZY",
          position: matcher.position,
          reusable: matcher.reusable,
          threshold: threshold ?? matcher.threshold,
          fuzzyKey: "",
        });
      }
      case "M_REGEX": {
        return new RegexMatcher({
          discriminator: "M_REGEX",
          position: matcher.position,
          reusable: matcher.reusable,
          threshold: threshold ?? matcher.threshold,
          regex: "",
        });
      }
      case "M_LOCATION": {
        return new LocationMatcher({
          discriminator: "M_LOCATION",
          position: matcher.position,
          reusable: matcher.reusable,
          threshold: threshold ?? matcher.threshold,
          section: PageSection.createTest({
            topLeft: { x: 10, y: 10 },
            bottomRight: { x: 20, y: 20 },
          }).toSectionData(),
          matcher: new RegexMatcher({
            discriminator: "M_REGEX",
            position: 0,
            reusable: true,
            threshold: threshold ?? matcher.threshold,
            regex: "",
          }).toMatcherData(),
        });
      }
      default: {
        throw new Error(`Unknown Matcher Type ${matcher.discriminator}`);
      }
    }
  }

  // TODO: rename to deconstructDlfMatcherDto
  static createDlfMatcher(matcher: DlfMatcherProperties): DlfMatcher {
    //console.trace('Creating DLFMatcher');
    switch (matcher.discriminator as DlfMatcherTypes & ColumnMatcherTypes) {
      case "F_DEFAULT": {
        return new DefaultValueMatcher(
          matcher as unknown as DefaultValueMatcherData,
        );
      }
      case "F_REGEX": {
        return new RegexFieldMatcher(
          matcher as unknown as RegexFieldMatcherData,
        );
      }
      case "F_KEYVALUE": {
        return new KeyValueMatcher(matcher as unknown as KeyValueMatcherData);
      }
      case "F_SECTION": {
        return new SectionMatcher(matcher as unknown as SectionMatcherData);
      }
      case "F_FUZZY": {
        return new FuzzyFieldMatcher(
          matcher as unknown as FuzzyFieldMatcherData,
        );
      }
      default: {
        throw new Error(`Unknown Matcher Type ${matcher.discriminator}`);
      }
    }
  }

  static createColumnMatcher(matcher: ColumnMatcherData): ColumnMatcher {
    switch (matcher.discriminator) {
      case "C_COLUMN": {
        return new ColumnMatcher(matcher as unknown as ColumnMatcherData);
      }
      default: {
        throw new Error(`Unknown Matcher Type ${matcher.discriminator}`);
      }
    }
  }

  static changeDlfMatcher(
    matcher: Readonly<DlfMatcherProperties>,
    newType: Readonly<DlfMatcherTypes>,
    threshold: number,
  ): DlfMatcher {
    //console.debug('Changing DLFMatcher type');
    switch (newType as DlfMatcherTypes) {
      case "F_DEFAULT": {
        return new DefaultValueMatcher({
          ocrConfidenceThreshold: -1,
          defaultValue: "",
          discriminator: "F_DEFAULT",
          fieldName: matcher.fieldName,
          position: matcher.position,
          reusable: matcher.reusable,
          threshold: threshold,
        });
      }
      case "F_REGEX": {
        return new RegexFieldMatcher({
          ocrConfidenceThreshold: matcher.ocrConfidenceThreshold,
          allowEmpty: false,
          discriminator: "F_REGEX",
          fieldName: matcher.fieldName,
          position: matcher.position,
          reusable: matcher.reusable,
          regex: "",
          threshold: threshold,
        });
      }
      case "F_KEYVALUE": {
        return new KeyValueMatcher({
          ocrConfidenceThreshold: matcher.ocrConfidenceThreshold,
          allowEmpty: false,
          discriminator: "F_KEYVALUE",
          fieldName: matcher.fieldName,
          position: matcher.position,
          reusable: matcher.reusable,
          threshold: threshold,
          keyMatcher: SectionMatcher.createTest({
            ocrConfidenceThreshold: matcher.ocrConfidenceThreshold,
            threshold: threshold,
            matcher: RegexFieldMatcher.createTest({
              ocrConfidenceThreshold: matcher.ocrConfidenceThreshold,
              threshold: threshold,
            }),
            section: PageSection.createTest({
              topLeft: {
                x: 10.0,
                y: 10.0,
              },
              bottomRight: {
                x: 20.0,
                y: 13.0,
              },
            }),
          }),
          valueMatcher: SectionMatcher.createTest({
            ocrConfidenceThreshold: matcher.ocrConfidenceThreshold,
            threshold: threshold,
            matcher: RegexFieldMatcher.createTest({
              ocrConfidenceThreshold: matcher.ocrConfidenceThreshold,
              threshold: threshold,
              regex: ".+",
            }),
            section: PageSection.createTest({
              topLeft: {
                x: 25.0,
                y: 10.0,
              },
              bottomRight: {
                x: 35.0,
                y: 13.0,
              },
            }),
          }),
        });
      }
      case "F_FUZZY": {
        return new FuzzyFieldMatcher({
          ocrConfidenceThreshold: matcher.ocrConfidenceThreshold,
          allowEmpty: false,
          discriminator: "F_FUZZY",
          fieldName: matcher.fieldName,
          position: matcher.position,
          reusable: matcher.reusable,
          fuzzyKey: "",
          threshold: threshold,
        });
      }
      case "F_SECTION":
        throw new Error(`Unsupported Matcher Type ${matcher.discriminator}`);
      default: {
        throw new Error(`Unknown Matcher Type ${matcher.discriminator}`);
      }
    }
  }

  static defaultMatcherFrom(docFieldConfig: DocFieldConfig): DefaultMatcher {
    return this.defaultMatcher(
      docFieldConfig.fieldName,
      docFieldConfig.threshold,
    );
  }

  static defaultMatcher(fieldName: string, threshold: number): DefaultMatcher {
    return new KeyValueMatcher({
      ocrConfidenceThreshold: -1,
      threshold: threshold,
      discriminator: "F_KEYVALUE",
      fieldName: fieldName,
      allowEmpty: false,
      position: 0,
      reusable: true,
      keyMatcher: new SectionMatcher({
        ocrConfidenceThreshold: -1,
        threshold: threshold,
        allowEmpty: false,
        discriminator: "F_SECTION",
        fieldName: fieldName,
        matcher: new RegexFieldMatcher({
          ocrConfidenceThreshold: -1,
          threshold: threshold,
          allowEmpty: false,
          discriminator: "F_REGEX",
          fieldName: fieldName,
          position: 0,
          regex: "",
          reusable: true,
        }),
        position: 0,
        reusable: true,
        section: new PageSection({
          discriminator: "PAGE",
          topLeft: {
            x: 32.0,
            y: 28.0,
          },
          bottomRight: {
            x: 45.0,
            y: 30.5,
          },
        }),
      }),
      valueMatcher: new SectionMatcher({
        ocrConfidenceThreshold: -1,
        threshold: threshold,
        allowEmpty: false,
        discriminator: "F_SECTION",
        fieldName: fieldName,
        matcher: new RegexFieldMatcher({
          ocrConfidenceThreshold: -1,
          threshold: threshold,
          allowEmpty: false,
          discriminator: "F_REGEX",
          fieldName: fieldName,
          position: 0,
          regex: ".+",
          reusable: true,
        }),
        position: 0,
        reusable: true,
        section: new PageSection({
          discriminator: "PAGE",
          topLeft: {
            x: 48.0,
            y: 28.0,
          },
          bottomRight: {
            x: 61.0,
            y: 30.5,
          },
        }),
      }),
    });
  }
}

export { MatcherFactory };
